import { computed, Directive, input } from '@angular/core';
import { GRADIENTS } from '@shared/constants/gradients';
import { cn } from '@shared/helpers/cn';

@Directive({
  selector: '[appButton]',
  standalone: true,
  host: { '[class]': 'styles()' },
})
export class AppButtonDirective {
  design = input<'primary' | 'success' | 'secondary' | 'danger'>('primary');
  variant = input<'solid' | 'outlined' | 'ghost' | 'soft'>('solid');
  width = input<'full' | 'auto'>('auto');
  gradient = input<'purple' | 'green' | 'red' | 'blue' | 'yellow' | ''>('');
  size = input<'normal' | 'large'>('normal');
  tone = input<'default' | 'location'>('default');
  class = input<string>();

  #base =
    '!text-sm flex gap-2 items-center justify-center whitespace-nowrap transition-colors active:!scale-95 hover:!scale-105 !transition-all disabled:!pointer-events-none disabled:!bg-none';

  public styles = computed(() =>
    cn(
      this.#base,
      {
        'w-full': this.width() === 'full',
        'px-4 py-2 !rounded-lg': this.size() === 'normal',
        'px-4 py-3 flex-1 !rounded-xl': this.size() === 'large',

        // Variants
        'bg-purple-600 hover:bg-purple-700 disabled:!bg-gray-50 disabled:!text-gray-400 text-white':
          this.variant() === 'solid' &&
          this.design() === 'primary' &&
          this.tone() === 'default',
        '!bg-[var(--app-theme-primary)] !text-[var(--app-theme-primary-text)] hover:!bg-[var(--app-theme-primary-hover)] disabled:!bg-gray-50 disabled:!text-gray-400':
          this.variant() === 'solid' && this.tone() === 'location',
        // Outlined: applies to each outlined variant
        '!border rounded-xl disabled:!border-gray-200 disabled:!text-gray-400 disabled:bg-gray-50 bg-transparent':
          this.variant() === 'outlined',
        // base outlined
        '!border-purple-600 !text-purple-600  hover:!bg-purple-50 ':
          this.variant() === 'outlined' &&
          this.design() === 'primary' &&
          !this.gradient() &&
          this.tone() === 'default',

        '!border-[var(--app-theme-primary)] !text-[var(--app-theme-primary)] hover:!bg-[var(--app-theme-primary-soft)]':
          this.variant() === 'outlined' && this.tone() === 'location',

        '!border-red-400 !text-red-400 hover:!bg-red-50':
          this.variant() === 'outlined' &&
          this.design() === 'danger' &&
          !this.gradient() &&
          this.tone() === 'default',

        // blue outlined
        '!border !border-blue-600 !text-blue-600 rounded-xl hover:!bg-blue-50':
          this.variant() === 'outlined' &&
          this.design() === 'primary' &&
          this.gradient() === 'blue',

        // Ghost
        'text-gray-500 !hover:text-gray-700 transition-colors bg-transparent hover:!bg-gray-100':
          this.variant() === 'ghost' && this.tone() === 'default',
        '!text-[var(--app-theme-primary)] transition-colors bg-transparent hover:!bg-[var(--app-theme-primary-soft)]':
          this.variant() === 'ghost' && this.tone() === 'location',

        // Soft
        'border border-pink-500/30 bg-pink-500/10 hover:bg-pink-500/20 text-pink-500':
          this.variant() === 'soft',

        // Type
        'border border-gray-300 text-gray-700 transition-colors hover:bg-white disabled:!bg-gray-50 disabled:!text-gray-400 disabled:!border-none':
          this.design() === 'secondary' && this.variant() !== 'ghost',

        // Gradient
        'disabled:!bg-gray-50 disabled:!text-gray-400 text-white':
          this.gradient() !== '',
        [`${GRADIENTS.blue.base}`]:
          this.gradient() === 'blue' && this.variant() === 'solid',
        [`${GRADIENTS.red.base}  hover:from-red-700 hover:to-orange-700`]:
          this.gradient() === 'red',
        [`${GRADIENTS.purple.base} hover:from-purple-700 hover:to-pink-700`]:
          this.gradient() === 'purple',
        [`${GRADIENTS.green.base} hover:from-green-700 hover:to-emerald-700`]:
          this.gradient() === 'green',
        [`${GRADIENTS.yellow.base} hover:from-yellow-500 hover:to-yellow-700`]:
          this.gradient() === 'yellow',
      },
      this.class(),
    ),
  );
}
